/**
 * PasswordField — the password row of a slip, with the two affordances the old
 * auth screens were missing.
 *
 * 1. SHOW / HIDE. A user typing a 12-character passphrase on a phone keyboard,
 *    one-handed, in daylight, cannot verify it any other way. The toggle is a
 *    real `<button type="button">` with `aria-pressed` and a translated
 *    `aria-label`, at a 44px target — never an icon glued to the input.
 *
 * 2. A LENGTH COUNTER, not a strength meter. The server policy is exactly two
 *    rules: at least 12 characters (REQ-PRD-§11.1-C) and not present in the
 *    HIBP breach corpus. A five-segment "weak / fair / strong" bar would be
 *    inventing a third rule and moralising about it — and this audience is
 *    already anxious about doing something wrong. So the meter shows the one
 *    fact the client can actually know, as a figure in the instrument face
 *    (`.g-data`, tabular): `8 / 12`, with a check when the rule is met. The
 *    breach rule is stated as plain helper text because only the server can
 *    evaluate it.
 *
 * The figure is `--g-ink` in both states rather than green-when-satisfied:
 * `--g-success` (#3F8E5A) on white measures 4.0:1, under the 4.5:1 floor for
 * text this small. The check GLYPH carries the success colour instead — an
 * icon only has to clear 3:1.
 */
import { Check, Eye, EyeOff } from "lucide-react";
import { forwardRef, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { SLIP_INPUT_CLASS, SlipField } from "@/features/auth/components/slip";
import { ICON, ICON_STROKE, iconProps } from "@/lib/icons";

export interface PasswordFieldProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> {
  id: string;
  label: string;
  /** Translated helper text — the constraint, stated up front. */
  hint?: string;
  hintId?: string;
  /** Translated error text. Renders below the field and is announced. */
  error?: string;
  errorId?: string;
  errorTestId?: string;
  /** Current value length, for the counter. Omit to hide the counter. */
  valueLength?: number;
  /** Minimum length the counter counts toward (12 for new passwords). */
  minLength?: number;
  /** Rendered right of the label when there is no counter (e.g. "Forgot?"). */
  meta?: ReactElement | null;
  required?: boolean;
  /** Lands on the `<input>` — the e2e/vitest selectors point at the control. */
  "data-testid"?: string;
}

export const PasswordField = forwardRef<HTMLInputElement, PasswordFieldProps>(
  function PasswordField(
    {
      id,
      label,
      hint,
      hintId,
      error,
      errorId,
      errorTestId,
      valueLength,
      minLength,
      meta,
      required = true,
      disabled,
      ...inputProps
    },
    ref,
  ) {
    const { t } = useTranslation("auth");
    const [visible, setVisible] = useState(false);

    const showCounter = valueLength !== undefined && minLength !== undefined;
    const satisfied = showCounter && (valueLength ?? 0) >= (minLength ?? 0);

    const counter = showCounter ? (
      <span className="flex items-center gap-1">
        {satisfied ? (
          <Check
            {...iconProps("xs")}
            className="text-[var(--g-success)]"
            aria-hidden="true"
          />
        ) : null}
        <span
          aria-hidden="true"
          className="g-data text-[0.6875rem] font-medium text-[var(--g-ink)]"
        >
          {String(valueLength)}/{String(minLength)}
        </span>
        <span className="sr-only">
          {/* `typed`, never `count`: i18next treats a `count` interpolation as
              a plural selector and would go looking for `_one`/`_few`/`_many`
              variants of this key that do not exist. */}
          {t("common.passwordLengthA11y", {
            typed: valueLength,
            min: minLength,
          })}
        </span>
      </span>
    ) : null;

    return (
      <SlipField
        htmlFor={id}
        label={label}
        meta={counter ?? meta}
        hint={hint}
        hintId={hintId}
        error={error}
        errorId={errorId}
        errorTestId={errorTestId}
        required={required}
        requiredLabel={t("common.requiredField")}
        trailing={
          <button
            type="button"
            onClick={() => {
              setVisible((v) => !v);
            }}
            aria-pressed={visible}
            aria-controls={id}
            aria-label={
              visible ? t("common.hidePassword") : t("common.showPassword")
            }
            disabled={disabled}
            data-testid={id + "-reveal"}
            className={
              "-mr-1.5 grid size-11 shrink-0 place-items-center rounded-[var(--g-radius-sm)] " +
              "text-[var(--g-ink-2)] transition-colors hover:text-[var(--g-ink)] " +
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring " +
              "focus-visible:ring-offset-1 focus-visible:ring-offset-[var(--g-surface)] " +
              "disabled:cursor-not-allowed disabled:opacity-50"
            }
          >
            {visible ? (
              <EyeOff size={ICON.md} strokeWidth={ICON_STROKE} aria-hidden="true" />
            ) : (
              <Eye size={ICON.md} strokeWidth={ICON_STROKE} aria-hidden="true" />
            )}
          </button>
        }
      >
        <input
          {...inputProps}
          ref={ref}
          id={id}
          type={visible ? "text" : "password"}
          disabled={disabled}
          aria-required={required || undefined}
          aria-invalid={error !== undefined || undefined}
          className={SLIP_INPUT_CLASS}
        />
      </SlipField>
    );
  },
);

export default PasswordField;
